import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material'
import { Client, User, usersApi, clientsApi, settingsApi, AssignClientTeamInput, UserRole } from '../../services/api'
import { isAdminOrManager } from '../../utils/roles'

const roleLabels: Record<UserRole, string> = {
  admin: 'Admin',
  sales: 'Sales',
  client_success: 'Client Success',
  resume_writer: 'Resume Writer',
  coach: 'Coach',
  client: 'Client',
}

function formatRole(role: UserRole): string {
  return roleLabels[role] || role
}

interface AssignTeamDialogProps {
  open: boolean
  onClose: () => void
  client: Client | null
  onAssigned: (updatedClient: Client) => void
}

export default function AssignTeamDialog({
  open,
  onClose,
  client,
  onAssigned,
}: AssignTeamDialogProps) {
  const [clientSuccessUsers, setClientSuccessUsers] = useState<User[]>([])
  const [resumeWriterUsers, setResumeWriterUsers] = useState<User[]>([])
  const [coachUsers, setCoachUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    clientSuccess: '',
    resumeWriter: '',
    coach: '',
  })

  // Load users by role when dialog opens
  useEffect(() => {
    if (open) {
      loadUsers()
      // Pre-fill with existing assignments
      if (client) {
        setFormData({
          clientSuccess: client.clientSuccess || '',
          resumeWriter: client.resumeWriter || '',
          coach: client.coach || '',
        })
      }
    }
  }, [open, client])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const [allUsers, defaultCsRepSetting] = await Promise.all([
        usersApi.list({ status: 'active' }),
        settingsApi.get('defaultClientSuccessRep').catch(() => ({ key: 'defaultClientSuccessRep', value: null })),
      ])

      setClientSuccessUsers(allUsers.filter((u) => u.role === 'client_success' || isAdminOrManager(u)))
      setResumeWriterUsers(allUsers.filter((u) => u.role === 'resume_writer' || isAdminOrManager(u)))
      setCoachUsers(allUsers.filter((u) => u.role === 'coach' || isAdminOrManager(u)))

      // Pre-fill default CS rep if the client doesn't already have one assigned
      if (defaultCsRepSetting?.value && client && !client.clientSuccess) {
        setFormData((prev) => ({ ...prev, clientSuccess: defaultCsRepSetting.value || '' }))
      }
    } catch (err) {
      console.error('Failed to load users:', err)
      setError('Failed to load team members')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value })
    setError(null)
  }

  const handleSubmit = async () => {
    if (!client) return

    // At least one assignment is required
    if (!formData.clientSuccess && !formData.resumeWriter && !formData.coach) {
      setError('Please assign at least one team member')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const input: AssignClientTeamInput = {
        clientId: client.id,
      }

      // Only include changed fields
      if (formData.clientSuccess) input.clientSuccess = formData.clientSuccess
      if (formData.resumeWriter) input.resumeWriter = formData.resumeWriter
      if (formData.coach) input.coach = formData.coach

      const updatedClient = await clientsApi.assignTeam(input)
      onAssigned(updatedClient)
      onClose()
    } catch (err) {
      console.error('Failed to assign team:', err)
      setError(err instanceof Error ? err.message : 'Failed to assign team')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      clientSuccess: '',
      resumeWriter: '',
      coach: '',
    })
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Team for {client?.name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Assign team members to handle different stages of the client&apos;s journey.
              Each team member will be notified when their stage is reached.
            </Typography>

            <TextField
              label="Client Success Rep"
              select
              value={formData.clientSuccess}
              onChange={(e) => handleChange('clientSuccess', e.target.value)}
              fullWidth
              helperText="Handles Onboarding stage"
            >
              <MenuItem value="">
                <em>Not assigned</em>
              </MenuItem>
              {clientSuccessUsers.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name} ({formatRole(user.role)})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Resume Writer"
              select
              value={formData.resumeWriter}
              onChange={(e) => handleChange('resumeWriter', e.target.value)}
              fullWidth
              helperText="Handles North Star, Resume Writing, and Core Deliverables stages"
            >
              <MenuItem value="">
                <em>Not assigned</em>
              </MenuItem>
              {resumeWriterUsers.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name} ({formatRole(user.role)})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Coach"
              select
              value={formData.coach}
              onChange={(e) => handleChange('coach', e.target.value)}
              fullWidth
              helperText="Handles Strategy Session and LinkedIn Workshop stages"
            >
              <MenuItem value="">
                <em>Not assigned</em>
              </MenuItem>
              {coachUsers.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name} ({formatRole(user.role)})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Assign Team'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
